/**
 * Reading of the color options elements store as `[[position, color]]` (fills, outlines, shadows...). Texts and
 *  image placeholders share it so both read saved templates the same way.
 */
import { useColor } from '@/composables/color';
import { DEFAULT_SHADOW_FILL } from '@/angular-app/libs/utils/shadowDefaults';
import type { ColorObject } from '@/typings/libraries';
import type { ElementMapOption } from '@/components/DesignStudio/types';

const { getColorObj, getColorFromString, cmykToDecimalString } = useColor();

type ColorObjectFields = Partial<ColorObject> & Record<string, unknown>;

export function createColorObj(override?: ColorObjectFields): ColorObject & Record<string, unknown> {
    return Object.assign({ alpha: 1, hex: '#000000', cmyk: '0,0,0,1' }, override);
}

/** A color stored as a JSON object string comes back as the object; anything else as it is. */
export function parseColorFromProperty<T>(property: T): T | ColorObject {
    if (typeof property === 'string' && property.indexOf('{') > -1) {
        return JSON.parse(property);
    }
    return property;
}

/**
 * textProperties.fontsMap/fontColorsMap coming from a live import (as opposed to a JSON string from a saved
 *  template) can be either a Map or an array of [key, value] pairs; copy its entries into `target` in place,
 *  since reassigning the parameter passed into initializeFontsMap/initializeFontColorsMap would not be visible
 *  to the caller.
 */
export function copyMapEntries<K, V>(target: Map<K, V>, source: unknown): void {
    if (source instanceof Map) {
        source.forEach((value, key) => target.set(key, value));
    } else if (Array.isArray(source)) {
        source.forEach((entry) => {
            if (!Array.isArray(entry) || entry.length !== 2) return;
            target.set(entry[0], entry[1]);
        });
    }
}

/**
 * Builds a colors Map from a stored value, tolerating every shape found in saved templates: a Map, a JSON
 *  string, an array of [key, value] pairs, or a single flattened pair ([1, color] instead of [[1, color]] -
 *  written by older clones). `new Map(value)` throws on the flattened shape, and a throw here aborts the whole
 *  template load, so anything unrecognizable degrades to an empty Map instead.
 */
export function parseColorsMap<V = ElementMapOption>(source: unknown): Map<number, V> {
    if (source instanceof Map) return new Map(source);

    let entries = source;
    if (typeof entries === 'string') {
        try {
            entries = JSON.parse(entries);
        } catch {
            return new Map();
        }
    }
    if (!Array.isArray(entries)) return new Map();

    const isFlattenedPair = entries.length === 2 && !Array.isArray(entries[0]) && typeof entries[0] !== 'object';
    const map = new Map<number, V>();
    copyMapEntries(map, isFlattenedPair ? [entries] : entries);
    return map;
}

/**
 * The color a shadow falls back to when the element never had one picked. Every place that seeds a shadow
 *  (texts, image placeholders, and the map built below when the element has nothing stored) has to produce the
 *  exact same object, otherwise an element renders with one color and is saved with another.
 */
export function createDefaultShadowColor(): ColorObject & Record<string, unknown> {
    const shadowColorObj = getColorObj({ hex: DEFAULT_SHADOW_FILL, a: 1 }) ?? {
        hex: '#000000',
        a: 1,
        cmyk: { c: '0', m: '0', y: '0', k: '1' }
    };
    const { c, m, y, k } = shadowColorObj.cmyk;
    return createColorObj({ hex: shadowColorObj.hex, cmyk: `${c},${m},${y},${k}`, a: shadowColorObj.a });
}

/**
 * Shadow color options as a Map, from the serialized `[[position, color]]` string or from an already built
 *  Map. ALWAYS answers a Map: callers do `.get(1)` inside an async Promise executor, where a TypeError reaches
 *  no reject() and hangs the whole template load.
 */
export function getShadowColorsMap(elementProperties: { shadowColorsMap?: unknown }): Map<number, ElementMapOption> {
    const stored = elementProperties.shadowColorsMap;

    // Nothing stored: seed the option every element is expected to have
    if (!stored) {
        return new Map([[1, createDefaultShadowColor()]]);
    }

    // Returned as-is (not copied) so callers that mutate the element's own map still do
    if (stored instanceof Map) {
        return stored;
    }

    let entries: unknown = stored;
    if (typeof entries === 'string') {
        try {
            entries = JSON.parse(entries);
        } catch (error) {
            console.warn('Malformed shadowColorsMap, falling back to the default shadow color', error);
            entries = null;
        }
    }

    // Valid JSON is not necessarily the [[position, color]] shape
    if (!Array.isArray(entries)) {
        return new Map([[1, createDefaultShadowColor()]]);
    }

    const shadowColorsMap = new Map<number, ElementMapOption>();
    entries.forEach((entry) => {
        if (Array.isArray(entry)) shadowColorsMap.set(entry[0], entry[1]);
    });
    return shadowColorsMap;
}

/**
 * Outline color options of an image placeholder as a Map of color objects. Images saved before the options
 *  existed come with their single color as a hex string (see LegacyColorsMap in Turcos.Data).
 */
export function getOutlineColorsMap(elementProperties: { outlineColorsMap?: unknown }): Map<number, ElementMapOption> {
    const outlineColorsMap = parseColorsMap(elementProperties.outlineColorsMap);
    outlineColorsMap.forEach((color, position) => {
        if (typeof color !== 'string') return;

        try {
            const { hex, a, cmyk } = getColorFromString(color);
            outlineColorsMap.set(position, createColorObj({ hex, alpha: a, cmyk: cmykToDecimalString(cmyk) }));
        } catch (error) {
            console.warn(`Dropping the unreadable outline color ${color}`, error);
            outlineColorsMap.delete(position);
        }
    });
    return outlineColorsMap;
}
